/**
 * Sequencer Hooks
 * React Query hooks for sequencer generation and preview
 */

import { useMutation } from '@tanstack/react-query';
import { sequencerService } from '../services/sequencer';
import { playlistService } from '../services/playlists';
import type { Track } from '../types';
import type { SequencerGenerateRequest, SequencerTrack } from '../services/sequencer';

/**
 * Convert sequencer track to workbench Track format
 * Sequencer returns simplified track data that needs to be converted
 * to the full Track interface expected by the workbench
 */
function convertToWorkbenchTrack(track: SequencerTrack): Track {
  return {
    id: track.uri,
    uri: track.uri,
    name: track.name,
    artists: track.artists.map((artistName) => ({
      id: '', // Not provided by sequencer
      name: artistName,
      uri: '', // Not provided by sequencer
    })),
    album: {
      id: '', // Not provided by sequencer
      name: track.album,
      releaseDate: '', // Not provided by sequencer
      uri: '', // Not provided by sequencer
      artists: [], // Not provided by sequencer
    },
    durationMs: track.durationMs,
    explicit: false, // Not provided by sequencer
    popularity: null, // Not provided by sequencer
    isrc: null, // Not provided by sequencer
  };
}

/**
 * Hook to generate a sequence from slot configuration
 *
 * Behavior depends on request parameters:
 * - If `output_playlist` is set: Replaces current playlist (loads to workbench)
 * - If `name` is set but no `output_playlist`: Creates new playlist in Spotify
 *
 * @example
 * // Replace current playlist
 * mutate({
 *   name: '',
 *   output_playlist: 'current_playlist_id',
 *   slots: [...],
 * });
 *
 * // Create new playlist
 * mutate({
 *   name: 'My New Sequence',
 *   slots: [...],
 * });
 */
export function useGenerateSequence() {
  return useMutation({
    mutationFn: async (request: SequencerGenerateRequest) => {
      // Step 1: Generate sequence
      const result = await sequencerService.generate(request);

      // Step 2: Handle based on mode
      const tracks: Track[] = result.tracks.map(convertToWorkbenchTrack);

      if (request.output_playlist) {
        // Replace mode: Return result with tracks
        return {
          ...result,
          mode: 'replace' as const,
          tracks,
          playlistId: request.output_playlist,
        };
      } else if (request.name && request.name.trim()) {
        // Create new mode: Create playlist and add tracks
        const trackUris = tracks.map((t) => t.uri);

        // Create new playlist
        const newPlaylist = await playlistService.create({
          name: request.name,
          description: `Generated sequence with ${tracks.length} tracks`,
          public: false,
        });

        // Add tracks to new playlist
        if (trackUris.length > 0) {
          await playlistService.addTracks(newPlaylist.id, {
            uris: trackUris,
          });
        }

        // Fetch full playlist details (with tracks)
        const fullPlaylist = await playlistService.get(newPlaylist.id);

        return {
          ...result,
          mode: 'create' as const,
          tracks,
          playlistId: newPlaylist.id,
          playlist: fullPlaylist,
        };
      }

      // Fallback: Just return result (shouldn't happen with proper validation)
      return {
        ...result,
        mode: 'replace' as const,
        tracks,
      };
    },
  });
}

/**
 * Hook to preview a sequence configuration
 *
 * Returns estimated track count, duration, and any warnings
 * without actually generating the sequence
 *
 * @example
 * const { mutate, data, isPending } = usePreviewSequence();
 *
 * mutate({ name: 'Test', slots: [...], artistSeparation: 4 });
 *
 * if (data) {
 *   console.log(`Estimated ${data.estimatedTracks} tracks`);
 *   console.log(`Warnings: ${data.warnings.join(', ')}`);
 * }
 */
export function usePreviewSequence() {
  return useMutation({
    mutationFn: (request: SequencerGenerateRequest) =>
      sequencerService.preview(request),
  });
}
